import { useState, useEffect, useRef } from 'react';
import { getPositions, DUAL_VIEW } from '../../constants/graphPositions';
import ItemNode from './ItemNode';
import EmptySlotNode from './EmptySlotNode';
import SharedSlotNode from './SharedSlotNode';
import ConnectionLine from './ConnectionLine';
import ContextMenu from './ContextMenu';
import { DragProvider } from './DragContext';
import DragPhantom from './DragPhantom';
import { getCollectionContextMenuOptions } from '../../utils/collectionContextMenu';
import { useTheme } from '../../hooks/useTheme';

export default function GraphView({ collections, lockedCollectionId, activeCollectionId, setActiveCollectionId, onAddFilter, onItemClick, onDrop, onUnlink, onEmptySlotClick, onDeleteCollection, onUnselectCollection, sharedSlotPositions, setSharedSlotPositions }) {
  return (
    <DragProvider onDrop={onDrop}>
      <GraphViewContent
        collections={collections}
        lockedCollectionId={lockedCollectionId}
        activeCollectionId={activeCollectionId}
        setActiveCollectionId={setActiveCollectionId}
        onAddFilter={onAddFilter}
        onItemClick={onItemClick}
        onUnlink={onUnlink}
        onEmptySlotClick={onEmptySlotClick}
        onDeleteCollection={onDeleteCollection}
        onUnselectCollection={onUnselectCollection}
        sharedSlotPositions={sharedSlotPositions}
        setSharedSlotPositions={setSharedSlotPositions}
      />
      <DragPhantom />
    </DragProvider>
  );
}

function GraphViewContent({ collections, lockedCollectionId, activeCollectionId, setActiveCollectionId, onAddFilter, onItemClick, onUnlink, onEmptySlotClick, onDeleteCollection, onUnselectCollection, sharedSlotPositions, setSharedSlotPositions }) {
  if (!collections || collections.length === 0) {
    return (
      <div className="text-center">
        <svg className="w-24 h-24 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-xl text-gray-400 font-semibold">Select one or two collections to view them here</p>
        <p className="text-sm text-gray-500 mt-2">Choose collections from the sidebar to visualize their data</p>
      </div>
    );
  }

  // Single collection view
  if (collections.length === 1) {
    return (
      <SingleCollectionView
        collection={collections[0]}
        isLocked={collections[0].id === lockedCollectionId}
        activeCollectionId={activeCollectionId}
        setActiveCollectionId={setActiveCollectionId}
        onAddFilter={onAddFilter}
        onItemClick={onItemClick}
        onUnlink={onUnlink}
        onEmptySlotClick={onEmptySlotClick}
        onDeleteCollection={onDeleteCollection}
        onUnselectCollection={onUnselectCollection}
      />
    );
  }

  // Dual collection view
  return (
    <DualCollectionView
      collections={collections}
      lockedCollectionId={lockedCollectionId}
      activeCollectionId={activeCollectionId}
      setActiveCollectionId={setActiveCollectionId}
      onAddFilter={onAddFilter}
      onItemClick={onItemClick}
      onUnlink={onUnlink}
      onEmptySlotClick={onEmptySlotClick}
      onDeleteCollection={onDeleteCollection}
      onUnselectCollection={onUnselectCollection}
      sharedSlotPositions={sharedSlotPositions}
      setSharedSlotPositions={setSharedSlotPositions}
    />
  );
}

function SingleCollectionView({ collection, isLocked, activeCollectionId, setActiveCollectionId, onAddFilter, onItemClick, onUnlink, onEmptySlotClick, onDeleteCollection, onUnselectCollection }) {
  const { currentThemeType, themeTypes } = useTheme();
  const containerRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [contextMenu, setContextMenu] = useState(null);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        setIsCompact(width <= 620);
        setContainerDimensions({ width, height });
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isActive = activeCollectionId === collection.id;

  const handleCollectionClick = (e) => {
    e.stopPropagation();
    setActiveCollectionId(isActive ? null : collection.id);
  };

  const handleCollectionContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const collectionContextMenuOptions = [
    {
      label: 'Copy Title',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>,
      onClick: () => {
        navigator.clipboard.writeText(collection.title);
        setContextMenu(null);
      },
    },
    { divider: true },
    {
      label: 'Unselect',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>,
      onClick: () => {
        setContextMenu(null);
        onUnselectCollection?.(collection.id);
      },
    },
    {
      label: 'Filter Out',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>,
      onClick: () => onAddFilter?.({ type: 'exclude-collection', collectionId: collection.id, collectionTitle: collection.title }),
    },
    { divider: true },
    {
      label: 'Delete',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>,
      onClick: () => {
        setContextMenu(null);
        onDeleteCollection?.(collection.id);
      },
      danger: true,
    },
  ];

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div ref={containerRef} className="relative w-full h-full max-w-6xl">
      {/* Collection Node - Center */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        <div
          onClick={handleCollectionClick}
          onContextMenu={handleCollectionContextMenu}
          className={`relative ${isLightTheme ? 'bg-white' : 'bg-gradient-to-br from-purple-600 to-blue-600'} rounded-2xl px-8 py-6 shadow-2xl border-2 min-w-[150px] max-w-xs select-none cursor-pointer transition-all duration-200 ${isActive ? 'border-yellow-400 scale-105 shadow-yellow-400/50' : isLightTheme ? 'border-rose-300 hover:border-rose-400 hover:scale-105 shadow-rose-200/50' : 'border-purple-400 hover:border-purple-300 hover:scale-105'}`} style={{ color: 'var(--color-text-on-accent)' }}>
                    {/* Collection Label */}
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${isLightTheme ? 'bg-rose-50 border-rose-200' : 'bg-gray-800 border-purple-400'} px-3 py-1 rounded-full border`}>
            <p className={`text-xs ${isLightTheme ? 'text-rose-600' : 'text-purple-300'} uppercase tracking-wide font-semibold`}>Collection</p>
          </div>
          {/* Selection Indicator - Lock or White Circle */}
          {isLocked ? (
            <div className="absolute -top-3 -right-3">
              <svg className={`w-7 h-7 ${isLightTheme ? 'text-amber-600' : 'text-amber-400 drop-shadow-[0_0_12px_rgba(251,191,36,0.8)]'}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1a5 5 0 00-5 5v4H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm-3 5a3 3 0 116 0v4H9V6zm3 9a2 2 0 110-4 2 2 0 010 4z"/>
              </svg>
            </div>
          ) : (
            <div className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-gradient-to-br from-white to-gray-100 border-2 border-gray-300 shadow-lg"></div>
          )}
          {collection.title === "Generating title..." ? (
            <div className="flex flex-col items-center justify-center gap-2.5 py-1">
              <div className="flex items-center gap-2">
                <svg className={`animate-spin h-5 w-5 ${isLightTheme ? 'text-rose-500' : 'text-purple-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className={`${isLightTheme ? 'text-gray-700' : 'text-white/80'} text-sm font-medium`}>Generating...</span>
              </div>
              <div className={`relative h-1.5 w-36 ${isLightTheme ? 'bg-rose-100' : 'bg-purple-900/30'} rounded-full overflow-hidden`}>
                <div className={`absolute inset-0 bg-gradient-to-r from-transparent ${isLightTheme ? 'via-rose-400/80' : 'via-purple-400/60'} to-transparent -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite]`}></div>
              </div>
            </div>
          ) : (
            <h3 className={`${isLightTheme ? 'text-gray-800' : 'text-white'} font-bold text-base text-center line-clamp-2`} title={collection.title}>{collection.title}</h3>
          )}
        </div>
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={collectionContextMenuOptions}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Loading indicator when slots are not available */}
      {!collection.slots && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 translate-y-12 text-gray-400 text-sm flex items-center space-x-2">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Loading items...</span>
        </div>
      )}

      {/* Item Nodes and Connection Lines */}
      {collection.slots?.map((slot) => {
        const position = getPositions.singleViewItem(slot.position);
        const hasItem = slot.item !== null;

        return (
          <div key={slot.position}>
            {/* Connection Line from center to item */}
            <ConnectionLine
              startX={50}
              startY={50}
              endX={(() => {
                let endX = position.left ? parseFloat(position.left) : (100 - parseFloat(position.right));
                const nodeWidth = isCompact ? 160 : 208;
                const offsetX = containerDimensions.width > 0 ? (nodeWidth / 2) / containerDimensions.width * 100 : 0;
                return position.left ? endX + offsetX : endX - offsetX;
              })()}
              endY={(() => {
                let endY = position.top ? parseFloat(position.top) : (100 - parseFloat(position.bottom));
                const nodeHeight = isCompact ? 96 : 128;
                const offsetY = containerDimensions.height > 0 ? (nodeHeight / 2) / containerDimensions.height * 100 : 0;
                return position.top ? endY + offsetY : endY - offsetY;
              })()}
              isFilled={hasItem}
            />

            {/* Item Node */}
            <div className="absolute z-20" style={position}>
              {hasItem ? (
                <ItemNode
                  item={slot.item}
                  collectionId={collection.id}
                  slotPosition={slot.position}
                  isCompact={isCompact}
                  slotId={`single-${slot.position}`}
                  isActive={isActive}
                  onAddFilter={onAddFilter}
                  onItemClick={onItemClick}
                  onUnlink={onUnlink}
                />
              ) : (
                <EmptySlotNode
                  collectionId={collection.id}
                  slotPosition={slot.position}
                  isCompact={isCompact}
                  slotId={`single-${slot.position}`}
                  onClick={() => onEmptySlotClick?.(collection.id)}
                />
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

function DualCollectionView({ collections, lockedCollectionId, activeCollectionId, setActiveCollectionId, onAddFilter, onItemClick, onUnlink, onEmptySlotClick, onDeleteCollection, onUnselectCollection, sharedSlotPositions, setSharedSlotPositions }) {
  const { currentThemeType, themeTypes } = useTheme();
  const containerRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  const [isGridLayout, setIsGridLayout] = useState(false);
  const [isUltraCompact, setIsUltraCompact] = useState(false);
  const [isMiniCompact, setIsMiniCompact] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [contextMenu, setContextMenu] = useState(null);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  // ========================================
  // SHARED ITEMS DETECTION
  // ========================================
  // Generate unique key for this collection pair (sorted for consistency)
  const collectionPairKey = collections.length === 2
    ? [collections[0].id, collections[1].id].sort().join('_')
    : null;

  // Arrays to track shared items (items that exist in both collections)
  const sharedItems = [];
  const collection1Items = new Map();
  const collection2Items = new Map();

  if (collections.length === 2 && collections[0].slots && collections[1].slots) {
    // Build a map of all items in collection 1
    collections[0].slots.forEach(slot => {
      if (slot.item) {
        collection1Items.set(slot.item.id, { item: slot.item, position: slot.position });
      }
    });

    // Find items that also exist in collection 2 (these are shared items)
    collections[1].slots.forEach(slot => {
      if (slot.item && collection1Items.has(slot.item.id)) {
        sharedItems.push({
          item: slot.item,
          collection1Position: collection1Items.get(slot.item.id).position,
          collection2Position: slot.position
        });
        collection2Items.set(slot.item.id, { item: slot.item, position: slot.position });
      }
    });
  }

  // Get shared slot positions for this collection pair from frontend state
  // This tracks which shared slot (0, 1, 2) each shared item appears in
  const currentSharedPositions = sharedSlotPositions[collectionPairKey] || {};

  // ========================================
  // AUTO-ASSIGN SHARED ITEMS TO SLOTS
  // ========================================
  // Automatically assign newly detected shared items to the next available shared slot (0-2)
  useEffect(() => {
    if (sharedItems.length > 0 && collectionPairKey) {
      const newPositions = { ...currentSharedPositions };
      let nextAvailableSlot = 0;

      // Assign each shared item to the next available shared slot
      sharedItems.forEach(({ item }) => {
        if (!Object.values(newPositions).includes(item.id)) {
          // Find next available slot (0, 1, or 2)
          while (nextAvailableSlot < 3 && Object.keys(newPositions).includes(String(nextAvailableSlot))) {
            nextAvailableSlot++;
          }
          if (nextAvailableSlot < 3) {
            newPositions[nextAvailableSlot] = item.id;
            nextAvailableSlot++;
          }
        }
      });

      // Update state only if positions changed
      if (JSON.stringify(newPositions) !== JSON.stringify(currentSharedPositions)) {
        setSharedSlotPositions(prev => ({
          ...prev,
          [collectionPairKey]: newPositions
        }));
      }
    }
  }, [sharedItems.length, collectionPairKey]);

  // ========================================
  // BUILD SHARED SLOTS ARRAY
  // ========================================
  // Create an array of 3 shared slots with their assigned items (or null if empty)
  const sharedSlots = [0, 1, 2].map(position => {
    const itemId = currentSharedPositions[position];
    const sharedItem = sharedItems.find(si => si.item.id === itemId);
    return {
      position,
      item: sharedItem ? sharedItem.item : null
    };
  });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        const compact = width <= 930;
        const gridLayout = width <= 750;
        const ultraCompact = height <= 920 || width <= 515;
        const miniCompact = width <= 415;

        console.log(`GraphView Resize: width=${width}, height=${height}, compact=${compact}, grid=${gridLayout}, ultra=${ultraCompact}, mini=${miniCompact}`);

        setIsCompact(compact);
        setIsGridLayout(gridLayout);
        setIsUltraCompact(ultraCompact);
        setIsMiniCompact(miniCompact);
        setContainerDimensions({ width, height });
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div ref={containerRef} className="relative w-full h-full max-w-6xl">
        {/* Render both collections */}
        {collections.map((collection, collectionIndex) => {
          const collectionPos = getPositions.dualViewCollection(collectionIndex, isUltraCompact, isGridLayout);
          const isActive = activeCollectionId === collection.id;
          const isLocked = collection.id === lockedCollectionId;

          const handleCollectionClick = (e) => {
            e.stopPropagation();
            setActiveCollectionId(isActive ? null : collection.id);
          };

          const handleCollectionContextMenu = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setContextMenu({ x: e.clientX, y: e.clientY, collectionId: collection.id });
          };

          const collectionContextMenuOptions = [
            {
              label: 'Copy Title',
              icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>,
              onClick: () => {
                navigator.clipboard.writeText(collection.title);
                setContextMenu(null);
              },
            },
            { divider: true },
            {
              label: 'Unselect',
              icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>,
              onClick: () => {
                setContextMenu(null);
                onUnselectCollection?.(collection.id);
              },
            },
            {
              label: 'Filter Out',
              icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 15.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>,
              onClick: () => onAddFilter?.({ type: 'exclude-collection', collectionId: collection.id, collectionTitle: collection.title }),
            },
            { divider: true },
            {
              label: 'Delete',
              icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>,
              onClick: () => {
                setContextMenu(null);
                onDeleteCollection?.(collection.id);
              },
              danger: true,
            },
          ];

          return (
            <div key={collection.id}>
              {/* Collection Node */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                style={collectionPos}
              >
                <div
                  onClick={handleCollectionClick}
                  onContextMenu={handleCollectionContextMenu}
                  className={`relative ${isLightTheme ? 'bg-white' : 'bg-gradient-to-br from-purple-600 to-blue-600'} rounded-2xl shadow-2xl border-2 max-w-xs select-none cursor-pointer transition-all duration-200 ${isUltraCompact ? 'px-3 py-2 min-w-[100px] rounded-xl' : 'px-6 py-4 min-w-[150px]'} ${isActive ? 'border-yellow-400 scale-105 shadow-yellow-400/50' : isLightTheme ? 'border-rose-300 hover:border-rose-400 hover:scale-105 shadow-rose-200/50' : 'border-purple-400 hover:border-purple-300 hover:scale-105'}`} style={{ color: 'var(--color-text-on-accent)' }}>
                                    {/* Collection Label */}
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${isLightTheme ? 'bg-rose-50 border-rose-200' : 'bg-gray-800 border-purple-400'} rounded-full border ${isUltraCompact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                    <p className={`${isLightTheme ? 'text-rose-600' : 'text-purple-300'} uppercase tracking-wide font-semibold ${isUltraCompact ? 'text-[8px]' : 'text-xs'}`}>Collection</p>
                  </div>
                  {/* Selection Indicator - Lock or White Circle */}
                  {isLocked ? (
                    <div className="absolute -top-3 -right-3">
                      <svg className={`${isLightTheme ? 'text-amber-600' : 'text-amber-400 drop-shadow-[0_0_12px_rgba(251,191,36,0.8)]'} ${isUltraCompact ? 'w-5 h-5' : 'w-7 h-7'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 1a5 5 0 00-5 5v4H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm-3 5a3 3 0 116 0v4H9V6zm3 9a2 2 0 110-4 2 2 0 010 4z"/>
                      </svg>
                    </div>
                  ) : (
                    <div className={`absolute -top-3 -right-3 rounded-full bg-gradient-to-br from-white to-gray-100 border-2 border-gray-300 shadow-lg ${isUltraCompact ? 'w-5 h-5' : 'w-7 h-7'}`}></div>
                  )}
                  {collection.title === "Generating title..." ? (
                    <div className={`flex flex-col items-center justify-center ${isUltraCompact ? 'gap-1' : 'gap-1.5'}`}>
                      <div className="flex items-center gap-1.5">
                        <svg className={`animate-spin ${isLightTheme ? 'text-rose-500' : 'text-purple-400'} ${isUltraCompact ? 'h-3 w-3' : 'h-4 w-4'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className={`${isLightTheme ? 'text-gray-700' : 'text-white/80'} font-medium ${isUltraCompact ? 'text-[9px]' : 'text-xs'}`}>Generating...</span>
                      </div>
                      <div className={`relative ${isLightTheme ? 'bg-rose-100' : 'bg-purple-900/30'} rounded-full overflow-hidden ${isUltraCompact ? 'h-0.5 w-16' : 'h-1 w-24'}`}>
                        <div className={`absolute inset-0 bg-gradient-to-r from-transparent ${isLightTheme ? 'via-rose-400/80' : 'via-purple-400/60'} to-transparent -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite]`}></div>
                      </div>
                    </div>
                  ) : (
                    <h3 className={`${isLightTheme ? 'text-gray-800' : 'text-white'} font-bold text-center line-clamp-2 ${isUltraCompact ? 'text-[10px]' : 'text-sm'}`} title={collection.title}>{collection.title}</h3>
                  )}
                </div>
              </div>

              {contextMenu && contextMenu.collectionId === collection.id && (
                <ContextMenu
                  x={contextMenu.x}
                  y={contextMenu.y}
                  options={collectionContextMenuOptions}
                  onClose={() => setContextMenu(null)}
                />
              )}

              {/* Loading indicator when slots are not available */}
              {!collection.slots && (
                <div
                  className="absolute transform -translate-x-1/2 text-gray-400 text-sm flex items-center space-x-2"
                  style={{
                    left: collectionPos.left,
                    top: `calc(${collectionPos.top} + 60px)`
                  }}
                >
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-xs">Loading...</span>
                </div>
              )}

              {/* Collection's Item Nodes - Render empty slots for shared items */}
              {collection.slots?.map((slot) => {
                // Check if this slot's item is shared (appears in both collections)
                // Shared items are rendered in the shared slots area, so we show empty here
                const isSharedItem = slot.item && sharedItems.some(si => si.item.id === slot.item.id);

                const position = isGridLayout
                  ? getPositions.dualViewItemGrid(collectionIndex, slot.position, isUltraCompact)
                  : getPositions.dualViewItem(collectionIndex, slot.position, isUltraCompact, isGridLayout);

                // Treat slot as empty if it contains a shared item (renders as EmptySlotNode)
                const hasItem = slot.item !== null && !isSharedItem;

                return (
                  <div key={`${collection.id}-${slot.position}`}>
                    {/* Connection Line from collection to item */}
                    <ConnectionLine
                      startX={50}
                      startY={collectionIndex === 0
                        ? (isUltraCompact ? DUAL_VIEW.ultraCompact.topCollectionY : (isGridLayout ? DUAL_VIEW.gridLayout.topCollectionY : DUAL_VIEW.topCollectionY))
                        : (isUltraCompact ? DUAL_VIEW.ultraCompact.bottomCollectionY : (isGridLayout ? DUAL_VIEW.gridLayout.bottomCollectionY : DUAL_VIEW.bottomCollectionY))}
                      endX={parseFloat(position.left)}
                      endY={parseFloat(position.top)}
                      isFilled={hasItem}
                      invertCurve={position.invertCurve || false}
                    />

                    {/* Item Node or Empty Slot */}
                    <div className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20" style={position}>
                      {hasItem ? (
                        <ItemNode
                          item={slot.item}
                          collectionId={collection.id}
                          slotPosition={slot.position}
                          isCompact={isCompact}
                          isUltraCompact={isUltraCompact}
                          isMiniCompact={isMiniCompact}
                          slotId={`dual-${collection.id}-${slot.position}`}
                          isActive={isActive}
                          onAddFilter={onAddFilter}
                          onItemClick={onItemClick}
                          onUnlink={onUnlink}
                        />
                      ) : (
                        <EmptySlotNode
                          collectionId={collection.id}
                          slotPosition={slot.position}
                          isCompact={isCompact}
                          isUltraCompact={isUltraCompact}
                          isMiniCompact={isMiniCompact}
                          slotId={`dual-${collection.id}-${slot.position}`}
                          onClick={() => onEmptySlotClick?.(collection.id)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Shared Slots (3 slots between the collections) */}
        {sharedSlots.map((slot) => {
          const position = isGridLayout
            ? getPositions.dualViewSharedSlotGrid(slot.position)
            : getPositions.dualViewSharedSlot(slot.position);
          const endX = parseFloat(position.left);
          const endY = parseFloat(position.top);
          const hasItem = slot.item !== null;

          return (
            <div key={`shared-${slot.position}`}>
              {/* Connection Lines from both collections to shared slot */}
              {/* Line from top collection */}
              <ConnectionLine
                startX={50}
                startY={isUltraCompact ? DUAL_VIEW.ultraCompact.topCollectionY : (isGridLayout ? DUAL_VIEW.gridLayout.topCollectionY : DUAL_VIEW.topCollectionY)}
                endX={endX}
                endY={endY}
                isFilled={hasItem}
                isShared={true}
              />

              {/* Line from bottom collection */}
              <ConnectionLine
                startX={50}
                startY={isUltraCompact ? DUAL_VIEW.ultraCompact.bottomCollectionY : (isGridLayout ? DUAL_VIEW.gridLayout.bottomCollectionY : DUAL_VIEW.bottomCollectionY)}
                endX={endX}
                endY={endY}
                isFilled={hasItem}
                isShared={true}
              />

              {/* Shared Slot Node */}
              <div className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20" style={position}>
                {hasItem ? (
                  <SharedSlotNode
                    item={slot.item}
                    collectionId1={collections[0].id}
                    collectionId2={collections[1].id}
                    sharedSlotPosition={slot.position}
                    isCompact={isCompact}
                    isUltraCompact={isUltraCompact}
                    isMiniCompact={isMiniCompact}
                    slotId={`shared-${slot.position}`}
                    isActive={false}
                    onAddFilter={onAddFilter}
                    onItemClick={onItemClick}
                    onUnlink={onUnlink}
                  />
                ) : (
                  <EmptySlotNode
                    collectionId="shared"
                    slotPosition={slot.position}
                    isCompact={isCompact}
                    isUltraCompact={isUltraCompact}
                    isMiniCompact={isMiniCompact}
                    isShared={true}
                    slotId={`shared-${slot.position}`}
                    onClick={() => onEmptySlotClick?.([collections[0].id, collections[1].id])}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
